import type {CssValue} from '../../constants'
import {deserializeFunction, serializeFunction} from './function'

const VAR_FUNCTION_NAME: string = 'var'

interface CssVarValue {
    variable: string
    value?: string
    prefix?: string
}

interface CssVar extends CssVarValue {
    defaultValue?: string
}

const buildVariableStr = ({variable, prefix}: CssVarValue): string => {
    if (!prefix) {
        return variable
    }

    return `${prefix}_${variable}`
}

const getVariableValue = (variableStr: string): CssVarValue | undefined => {
    const [match, variable, prefix, value] = variableStr.match(/^--((?:(.+?)_)?(.+))$/) ?? []
    if (!match) {
        return
    }

    return {
        variable,
        value,
        prefix
    }
}

export const getVarValueRegex = (prefix: string) => new RegExp(`^${prefix}_[0-9]+$`)

export const serializeVariable = ({variable, prefix, defaultValue}: CssVar): CssValue => {
    const args = [`--${buildVariableStr({variable, prefix})}`]
    if (defaultValue) {
        args.push(defaultValue)
    }

    return serializeFunction({name: VAR_FUNCTION_NAME, args})
}

export const deserializeVariable = (val: CssValue): CssVar | undefined => {
    const func = deserializeFunction(val)
    if (!func) {
        return undefined
    }

    const {name, args} = func
    if (name !== VAR_FUNCTION_NAME || args.length === 0 || args.length > 2) {
        return undefined
    }

    const [variableStr, defaultValue] = args
    const variableValue = getVariableValue(variableStr)
    if (!variableValue) {
        return undefined
    }

    const {variable, value, prefix} = variableValue

    return {
        variable,
        value,
        prefix,
        defaultValue
    }
}
